import ChapterBadge from "./ChapterBadge";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Lightning, 
  Clock, 
  Target, 
  ChartLineUp,
  TrendUp,
  FileText,
  GitBranch,
  Users
} from "@phosphor-icons/react";

const TwoPillars = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const executiveIntelligence = {
    title: "Executive Intelligence",
    description: "OnTenacity gives CXOs fast, source-backed briefs, insights, risks, and action points from across the enterprise.",
    questions: [
      "What changed?",
      "What needs attention?",
      "Where are risks building?",
      "What decisions are blocked?",
      "What should leadership know this morning?",
    ],
    outputs: [
      { icon: FileText, label: "Daily Brief" },
      { icon: Target, label: "Watchlist" },
      { icon: Lightning, label: "Action Points" },
      { icon: ChartLineUp, label: "Follow-up Questions" },
    ],
  };

  const enterpriseContext = {
    title: "Why OnTenacity Gets Stronger Over Time",
    description: "OnTenacity builds on persistent enterprise context — systems, reports, documents, prior decisions, and operating history — so every brief becomes faster, more grounded, and more useful over time.",
    features: [
      "Prior decisions strengthen future recommendations",
      "Reporting history improves continuity",
      "Project and operating context reduces repeated explanation",
      "Institutional context makes follow-up answers faster and sharper",
    ],
    outputs: [
      { icon: TrendUp, label: "Source History" },
      { icon: Clock, label: "Decision History" },
      { icon: GitBranch, label: "Operating Context" },
      { icon: Users, label: "Institutional Context" },
    ],
  };

  return (
    <section 
      id="pillars"
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative overflow-hidden"
      data-testid="two-pillars-section"
    >
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] radial-gradient opacity-30" />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <ChapterBadge number="07" name="Architecture" />
          <span className="mono-label">Intelligence + Context</span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit']">
            Fast intelligence. <span className="gradient-text">Persistent context.</span>
          </h2>
          <p className="mt-4 text-[#A1A1AA] max-w-2xl mx-auto text-base md:text-lg">
            Decision-ready briefs today. Stronger enterprise context over time.
          </p>
        </motion.div>

        {/* Pillars Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Executive Intelligence Pillar */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="pillar-card executive rounded-xl p-6 md:p-8"
            data-testid="pillar-executive-intelligence"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center">
                <Lightning size={22} weight="duotone" className="text-[#00E5FF]" />
              </div>
              <h3 className="text-2xl font-bold text-white font-['Outfit']">
                {executiveIntelligence.title}
              </h3>
            </div>

            <p className="text-[#A1A1AA] mb-6">
              {executiveIntelligence.description}
            </p>

            {/* Questions */}
            <div className="mb-6">
              <span className="text-xs text-[#00E5FF] uppercase tracking-wider font-medium">
                Questions Answered
              </span>
              <ul className="mt-3 space-y-2">
                {executiveIntelligence.questions.map((question, i) => (
                  <li 
                    key={i} 
                    className="flex items-center gap-2 text-sm text-[#FAFAFA]"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF]" />
                    {question}
                  </li>
                ))}
              </ul>
            </div>

            {/* Outputs */}
            <div className="grid grid-cols-2 gap-3">
              {executiveIntelligence.outputs.map((output, i) => (
                <div 
                  key={i}
                  className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/5"
                >
                  <output.icon size={18} className="text-[#00E5FF]" />
                  <span className="text-sm text-[#FAFAFA]">{output.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Enterprise Context Pillar */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="pillar-card memory rounded-xl p-6 md:p-8"
            data-testid="pillar-enterprise-context"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#06B6D4]/20 flex items-center justify-center">
                <TrendUp size={22} weight="duotone" className="text-[#06B6D4]" />
              </div>
              <h3 className="text-xl font-bold text-white font-['Outfit']">
                {enterpriseContext.title}
              </h3>
            </div>

            <p className="text-[#A1A1AA] mb-6 text-sm">
              {enterpriseContext.description}
            </p>

            {/* Features */}
            <div className="mb-6">
              <span className="text-xs text-[#06B6D4] uppercase tracking-wider font-medium">
                Context That Compounds
              </span>
              <ul className="mt-3 space-y-2">
                {enterpriseContext.features.map((feature, i) => (
                  <li 
                    key={i} 
                    className="flex items-center gap-2 text-sm text-[#FAFAFA]"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#06B6D4]" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Outputs */}
            <div className="grid grid-cols-2 gap-3">
              {enterpriseContext.outputs.map((output, i) => (
                <div 
                  key={i}
                  className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/5"
                >
                  <output.icon size={18} className="text-[#06B6D4]" />
                  <span className="text-sm text-[#FAFAFA]">{output.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Connecting Visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-8 flex justify-center items-center"
        >
          <div className="flex items-center gap-4 px-6 py-3 rounded-full bg-white/5 border border-white/10">
            <div className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse" />
            <span className="text-sm text-[#A1A1AA]">Shared Enterprise Context</span>
            <div className="w-2 h-2 rounded-full bg-[#06B6D4] animate-pulse" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TwoPillars;
